import {
  Button,
  Card,
  CardContent,
  FormField,
  Input,
  PageHeader,
  SelectField,
  StatusChip,
  SwitchField,
} from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useOwnerState, useUpdateMenuItem } from "../../features/backoffice/queries.ts";
import type { ModifierGroup, ModifierOption } from "../../features/backoffice/types.ts";

export const Route = createFileRoute("/_app/menu/$itemId")({ component: MenuDetailScreen });

function MenuDetailScreen() {
  const { itemId } = Route.useParams();
  const { data } = useOwnerState();
  const update = useUpdateMenuItem();
  const item = data?.menuItems.find((row) => row.id === itemId);
  const categories = data?.categories ?? [];
  const [draft, setDraft] = useState({
    name: "",
    nameTh: "",
    description: "",
    priceMinor: 0,
    categoryId: "",
  });
  useEffect(() => {
    if (!item) return;
    setDraft({
      name: item.name,
      nameTh: item.nameTh,
      description: item.description,
      priceMinor: item.priceMinor,
      categoryId: item.categoryId,
    });
  }, [item]);

  if (!item) return <PageHeader title="Menu item" subtitle="Loading item…" />;
  const setField = (key: keyof typeof draft, value: string | number) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const categoryOptions = categories.map((category) => ({
    value: category.id,
    label: category.name,
  }));
  const saveDraft = () => update.mutate({ itemId, input: draft });
  const updateModifiers = (modifiers: ModifierGroup[]) =>
    update.mutate({ itemId, input: { modifiers } });
  const addModifierGroup = () =>
    updateModifiers([
      ...item.modifiers,
      {
        id: `mod-${Date.now().toString(36)}`,
        name: "New modifier group",
        required: false,
        multiSelect: false,
        options: [],
      },
    ]);
  const patchModifierGroup = (groupId: string, patch: Partial<ModifierGroup>) =>
    updateModifiers(
      item.modifiers.map((group) => (group.id === groupId ? { ...group, ...patch } : group)),
    );
  const addModifierOption = (groupId: string) =>
    updateModifiers(
      item.modifiers.map((group) =>
        group.id === groupId
          ? {
              ...group,
              options: [
                ...group.options,
                {
                  id: `opt-${Date.now().toString(36)}`,
                  name: "New option",
                  priceDeltaMinor: 0,
                },
              ],
            }
          : group,
      ),
    );
  const patchModifierOption = (groupId: string, optionId: string, patch: Partial<ModifierOption>) =>
    updateModifiers(
      item.modifiers.map((group) =>
        group.id === groupId
          ? {
              ...group,
              options: group.options.map((option) =>
                option.id === optionId ? { ...option, ...patch } : option,
              ),
            }
          : group,
      ),
    );

  return (
    <div className="space-y-6">
      <PageHeader
        title={item.name}
        subtitle="API_READY local DTO until generated item type includes owner fields."
      />
      <Card>
        <CardContent className="grid gap-4 p-5 md:grid-cols-2">
          <FormField id="name" label="Name EN">
            <Input
              id="name"
              value={draft.name}
              onChange={(event) => setField("name", event.target.value)}
            />
          </FormField>
          <FormField id="nameTh" label="Name TH">
            <Input
              id="nameTh"
              value={draft.nameTh}
              onChange={(event) => setField("nameTh", event.target.value)}
            />
          </FormField>
          <SelectField
            id="categoryId"
            label="Category"
            value={draft.categoryId}
            onChange={(value) => setField("categoryId", value)}
            options={categoryOptions}
          />
          <FormField id="price" label="Price minor unit (satang)">
            <Input
              id="price"
              type="number"
              value={draft.priceMinor}
              onChange={(event) => setField("priceMinor", Number(event.target.value))}
            />
          </FormField>
          <FormField id="description" label="Description">
            <Input
              id="description"
              value={draft.description}
              onChange={(event) => setField("description", event.target.value)}
            />
          </FormField>
          <SwitchField
            label="Available"
            checked={item.available}
            onChange={(checked) => update.mutate({ itemId, input: { available: checked } })}
          />
          <SwitchField
            label="Giftable"
            checked={item.giftable}
            onChange={(checked) => update.mutate({ itemId, input: { giftable: checked } })}
          />
          <SwitchField
            label="Alcohol"
            checked={item.alcohol}
            onChange={(checked) => update.mutate({ itemId, input: { alcohol: checked } })}
          />
          <SwitchField
            label="Featured giftable"
            checked={item.featuredGiftable}
            onChange={(checked) => update.mutate({ itemId, input: { featuredGiftable: checked } })}
          />
          <div className="md:col-span-2">
            <Button onClick={saveDraft}>Save item details</Button>
            {item.inFlightWarning ? (
              <p className="mt-3 rounded-md bg-amber/10 p-3 text-sm">
                {item.inFlightWarning}: deleting/changing price needs confirmation in production.
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display font-semibold text-lg">Modifiers and options</h2>
              <p className="text-muted-foreground text-sm">
                Mock-only option setup for required add-ons, multi-select groups, and price deltas.
              </p>
            </div>
            <Button onClick={addModifierGroup}>Add modifier group</Button>
          </div>
          {item.modifiers.length ? (
            item.modifiers.map((group) => (
              <div key={group.id} className="space-y-3 rounded-lg border border-border p-4">
                <div className="grid gap-3 md:grid-cols-[1fr_auto_auto_auto]">
                  <FormField id={`${group.id}-name`} label="Group name">
                    <Input
                      id={`${group.id}-name`}
                      defaultValue={group.name}
                      onBlur={(event) => patchModifierGroup(group.id, { name: event.target.value })}
                    />
                  </FormField>
                  <SwitchField
                    label="Required"
                    checked={group.required}
                    onChange={(checked) => patchModifierGroup(group.id, { required: checked })}
                  />
                  <SwitchField
                    label="Multi select"
                    checked={group.multiSelect}
                    onChange={(checked) => patchModifierGroup(group.id, { multiSelect: checked })}
                  />
                  <div className="flex items-end">
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-foreground"
                      onClick={() => addModifierOption(group.id)}
                    >
                      Add option
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {group.options.length ? (
                    group.options.map((option) => (
                      <div
                        key={option.id}
                        className="grid gap-2 rounded-md bg-muted/30 p-3 md:grid-cols-[1fr_10rem]"
                      >
                        <FormField id={`${option.id}-name`} label="Option name">
                          <Input
                            id={`${option.id}-name`}
                            defaultValue={option.name}
                            onBlur={(event) =>
                              patchModifierOption(group.id, option.id, {
                                name: event.target.value,
                              })
                            }
                          />
                        </FormField>
                        <FormField id={`${option.id}-delta`} label="Delta satang">
                          <Input
                            id={`${option.id}-delta`}
                            type="number"
                            defaultValue={option.priceDeltaMinor}
                            onBlur={(event) =>
                              patchModifierOption(group.id, option.id, {
                                priceDeltaMinor: Number(event.target.value),
                              })
                            }
                          />
                        </FormField>
                      </div>
                    ))
                  ) : (
                    <StatusChip tone="neutral">No options yet</StatusChip>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-muted-foreground text-sm">
              No modifier groups yet. Add one to model choices like sweetness, mixer, or garnish.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
